import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import overviewImg from "../assets/compare/01-compare-page-overview.png";
import inputsImg from "../assets/compare/02-inputs-filled.png";
import resultsImg from "../assets/compare/03-results-cards.png";
import detailsImg from "../assets/compare/04-card-details.png";
import abc_phoneImg from "../assets/compare/05-abc_phone-login.png";
import abc_6numberImg from "../assets/compare/06-abc_6number-login.png";
import abc_fetchbImg from "../assets/compare/07-abc_fetch_button-login.png";

const STEP_SETS = {
  compareFlow: [
    { key: "overview", image: overviewImg },
    { key: "abc_phone", image: abc_phoneImg },
    { key: "inputs", image: inputsImg },
    { key: "abc_6number", image: abc_6numberImg },
    { key: "results", image: resultsImg },
    { key: "details", image: detailsImg },
  ],
  abcList: [
    { key: "abc_phone", image: abc_phoneImg },
    { key: "abc_fetchb", image: abc_fetchbImg },
    { key: "abc_6number", image: abc_6numberImg },
  ],
};

export default function CompareHelp() {
  const { t } = useTranslation();
  const [lightbox, setLightbox] = useState(null);
  const [activeTab, setActiveTab] = useState("compareFlow");

  const tabs = useMemo(
    () => [
      {
        key: "compareFlow",
        label: t("help.compare.tabs.compareFlow.label"),
        description: t("help.compare.tabs.compareFlow.description"),
      },
      {
        key: "abcList",
        label: t("help.compare.tabs.abcList.label"),
        description: t("help.compare.tabs.abcList.description"),
      },
    ],
    [t]
  );

  const steps = STEP_SETS[activeTab] || [];

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 uppercase tracking-wide">
            {t("help.compare.breadcrumb")}
          </p>
          <h1 className="text-2xl font-bold">{t("help.compare.title")}</h1>
        </div>
        <Link
          to="/"
          className="text-sm text-blue-600 hover:underline"
        >
          {t("help.compare.back")}
        </Link>
      </div>

      <p className="text-gray-700">
        {tabs.find((tab) => tab.key === activeTab)?.description || t("help.compare.description")}
      </p>

      <div className="flex flex-wrap gap-2">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`px-3 py-1.5 rounded-full text-sm border ${
              activeTab === tab.key
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white text-gray-700 border-gray-300 hover:border-gray-400"
            }`}
            onClick={() => {
              setActiveTab(tab.key);
              setLightbox(null);
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="space-y-8">
        {steps.map((step, idx) => (
          <div
            key={step.key}
            className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start"
          >
            <div className="space-y-2">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                {t("help.compare.stepLabel", { number: idx + 1 })}
              </div>
              <h2 className="text-lg font-semibold">
                {t(`help.compare.steps.${step.key}.title`)}
              </h2>
              <p className="text-sm text-gray-700">
                {t(`help.compare.steps.${step.key}.body`)}
              </p>
            </div>
            {step.image && (
              <div className="border rounded-lg bg-white shadow-sm overflow-hidden">
                <img
                  src={step.image}
                  alt={t(`help.compare.steps.${step.key}.alt`)}
                  className="w-full object-contain max-h-[420px] bg-gray-50 cursor-zoom-in"
                  onClick={() => setLightbox({ ...step, index: idx })}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-40 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          role="presentation"
        >
          <div
            className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
            role="presentation"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {t("help.compare.stepLabel", { number: (lightbox.index || 0) + 1 })}
                </div>
                <h3 className="text-lg font-semibold">
                  {t(`help.compare.steps.${lightbox.key}.title`)}
                </h3>
              </div>
              <button
                type="button"
                className="text-sm text-gray-600 hover:text-black"
                onClick={() => setLightbox(null)}
              >
                ✕
              </button>
            </div>
            <div className="p-4 bg-gray-50">
              <img
                src={lightbox.image}
                alt={t(`help.compare.steps.${lightbox.key}.alt`)}
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
